import React, { useState, useEffect, useCallback } from 'react';
import UserManagementTable from './UserManagementTable';
import adminService from '../services/adminService';
import { toast } from 'react-toastify';

function AdminUsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      console.log('[AdminUsersTab] Fetching users from API');
      
      let userData;
      
      try {
        // Try to get users from the standard path
        const data = await adminService.getAllUsers();
        userData = data;
        console.log('[AdminUsersTab] Successfully fetched users:', data);
      } catch (apiError) {
        if (apiError.response?.status === 404) {
          // Try alternate path with direct /api prefix if we get a 404
          console.log('[AdminUsersTab] 404 error, trying alternate path /api/admin/users');
          
          try {
            const response = await fetch('/api/admin/users', {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            
            if (!response.ok) {
              throw new Error(`API error: ${response.status}`);
            }
            
            userData = await response.json();
            console.log('[AdminUsersTab] Successfully fetched users from alternate path:', userData);
          } catch (alternateError) {
            console.error('[AdminUsersTab] Both paths failed:', apiError, alternateError);
            throw new Error('Failed to fetch users from both API paths');
          }
        } else {
          // If it's not a 404 error, just re-throw
          throw apiError;
        }
      }
      
      // If we have user data, process it
      console.log('[AdminUsersTab] Raw user data from API:', userData);
      
      // Handle the specific structure from our backend
      if (userData && userData.users && userData.users.results && Array.isArray(userData.users.results)) {
        console.log('[AdminUsersTab] Transforming API user data format from users.results');
        
        // Transform backend snake_case to frontend camelCase
        const transformedUsers = userData.users.results.map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          organization: user.organization || '',
          role: user.role,
          isActive: user.is_active === 1, // Convert numeric 1/0 to boolean
          createdAt: user.created_at || new Date().toISOString(),
          // Add any missing properties with defaults
          lastLogin: user.last_login || user.updated_at || user.created_at || new Date().toISOString()
        }));
        
        console.log('[AdminUsersTab] Transformed user data:', transformedUsers);
        setUsers(transformedUsers);
      } else if (userData && userData.results && Array.isArray(userData.results)) {
        // Alternative structure where results is at the top level
        console.log('[AdminUsersTab] Transforming API user data format from top-level results');
        
        const transformedUsers = userData.results.map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          organization: user.organization || '',
          role: user.role,
          isActive: user.is_active === 1, // Convert numeric 1/0 to boolean
          createdAt: user.created_at || new Date().toISOString(),
          lastLogin: user.last_login || user.updated_at || user.created_at || new Date().toISOString()
        }));
        
        setUsers(transformedUsers);
      } else if (userData && Array.isArray(userData.users)) {
        // If users property is directly an array
        console.log('[AdminUsersTab] Transforming API user data format from users array');
        
        const transformedUsers = userData.users.map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          organization: user.organization || '',
          role: user.role,
          isActive: user.is_active === 1, // Convert numeric 1/0 to boolean
          createdAt: user.created_at || new Date().toISOString(),
          lastLogin: user.last_login || user.updated_at || user.created_at || new Date().toISOString()
        }));
        
        setUsers(transformedUsers);
      } else if (userData && Array.isArray(userData)) {
        // If it's just a plain array
        console.log('[AdminUsersTab] Transforming API user data format from plain array');
        
        const transformedUsers = userData.map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          organization: user.organization || '',
          role: user.role,
          isActive: user.is_active === 1, // Convert numeric 1/0 to boolean
          createdAt: user.created_at || new Date().toISOString(),
          lastLogin: user.last_login || user.updated_at || user.created_at || new Date().toISOString()
        }));
        
        setUsers(transformedUsers);
      } else {
        // Use mock data as fallback if API returned invalid format
        console.warn('[AdminUsersTab] API returned invalid data format, using mock data');
        toast.warning('Using mock data due to API format mismatch');
        setUsers([
          {
            id: 'user-123',
            name: 'John Doe',
            email: 'john@greenfieldsfarm.co.ke',
            organization: 'Green Fields Farm',
            role: 'donor',
            isActive: true,
            createdAt: '2025-01-15T10:30:00Z',
            lastLogin: '2025-06-01T08:45:00Z'
          },
          {
            id: 'user-202',
            name: 'David Kamau',
            email: 'david@wastenot.co.ke',
            organization: 'WasteNot Admin',
            role: 'admin',
            isActive: true,
            createdAt: '2025-01-01T08:00:00Z',
            lastLogin: '2025-06-03T09:10:00Z'
          }
        ]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleUserStatusChange = useCallback(async (userId, isActive) => {
    try {
      console.log(`[AdminUsersTab] Updating user ${userId} status to ${isActive ? 'active' : 'inactive'}`);
      
      try {
        // Try to update user status via standard adminService path
        await adminService.updateUserStatus(userId, { isActive });
        toast.success(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
      } catch (apiError) {
        if (apiError.response?.status === 404) {
          // Try alternate path with direct /api prefix
          console.log(`[AdminUsersTab] 404 error, trying alternate path /api/admin/users/${userId}/status`);
          try {
            const response = await fetch(`/api/admin/users/${userId}/status`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({ isActive })
            });
            
            if (!response.ok) {
              throw new Error(`API error: ${response.status}`);
            }
            
            toast.success(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
          } catch (alternateError) {
            console.error('[AdminUsersTab] Both paths failed:', apiError, alternateError);
            throw new Error('Failed to update user status through both API paths');
          }
        } else {
          // If it's not a 404 error, re-throw
          throw apiError;
        }
      }
      
      // Update local state regardless of which API path succeeded
      setUsers(users => users.map(user => {
        if (user.id === userId) {
          return { ...user, isActive };
        }
        return user;
      }));
      
      console.log(`User ${userId} status updated to ${isActive ? 'active' : 'inactive'}`);
    } catch (err) {
      console.error('Error updating user status:', err);
      toast.error(`Failed to ${isActive ? 'activate' : 'deactivate'} user. Please try again.`);
    }
  }, []);

  // Render loading state
  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading users...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <UserManagementTable
        users={users}
        onUserStatusChange={handleUserStatusChange}
      />
    </div>
  );
}

export default AdminUsersTab;
